import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Card, { CardHeader } from '../../../components/ui/Card'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import Modal from '../../../components/ui/Modal'
import Spinner from '../../../components/ui/Spinner'
import api from '../../../lib/api'
import { formatPhone, formatCurrency, formatDate } from '../../../lib/formatters'
import type { WorkOrder, Invoice } from '../../../types'

interface DoctorResponse {
  id: string
  code: string
  name: string
  clinic_name?: string
  phone: string
  email?: string
  gstin?: string
  state_code?: string
  pan?: string
  notes?: string
  is_active: boolean
  addresses: Array<{
    id: string
    label: string
    address_line1: string
    address_line2?: string
    city: string
    state: string
    state_code: string
    pincode: string
    is_default: boolean
  }>
  created_at: string
  updated_at: string
}

interface DoctorDisplay {
  id: string
  code: string
  name: string
  clinicName?: string
  phone: string
  email?: string
  gstNumber?: string
  panNumber?: string
  isActive: boolean
  addresses: Array<{
    id: string
    label: string
    line1: string
    line2?: string
    city: string
    state: string
    stateCode: string
    pincode: string
    isDefault: boolean
  }>
}

export default function DoctorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddAddressModal, setShowAddAddressModal] = useState(false)

  const { data: doctor, isLoading } = useQuery({
    queryKey: ['doctors', id],
    queryFn: async () => {
      const { data } = await api.get<{ data: DoctorResponse }>(`/doctors/${id}`)
      const d = data.data
      // Transform snake_case to camelCase
      return {
        id: d.id,
        code: d.code,
        name: d.name,
        clinicName: d.clinic_name,
        phone: d.phone,
        email: d.email,
        gstNumber: d.gstin,
        panNumber: d.pan,
        isActive: d.is_active,
        addresses: d.addresses?.map(a => ({
          id: a.id,
          label: a.label,
          line1: a.address_line1,
          line2: a.address_line2,
          city: a.city,
          state: a.state,
          stateCode: a.state_code,
          pincode: a.pincode,
          isDefault: a.is_default,
        })) ?? [],
      } as DoctorDisplay
    },
  })

  const { data: recentOrders } = useQuery({
    queryKey: ['doctors', id, 'orders'],
    queryFn: async () => {
      const { data } = await api.get<{ data: WorkOrder[] }>(`/work-orders?doctorId=${id}&limit=5`)
      return data.data
    },
  })

  const { data: recentInvoices } = useQuery({
    queryKey: ['doctors', id, 'invoices'],
    queryFn: async () => {
      const { data } = await api.get<{ data: Invoice[] }>(`/invoices?doctorId=${id}&limit=5`)
      return data.data
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!doctor) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-user-slash text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-500 dark:text-gray-400">Doctor not found</p>
        <Link to="/doctors" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Doctors
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/doctors"
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <i className="fas fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{doctor.name}</h1>
              <Badge variant="info">{doctor.code}</Badge>
              <Badge variant={doctor.isActive ? 'success' : 'gray'}>
                {doctor.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-gray-600 dark:text-gray-400">{doctor.clinicName || 'No clinic name'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            <i className="fas fa-edit mr-2"></i>
            Edit
          </Button>
          <Button onClick={() => window.location.href = `/work-orders/new?doctorId=${id}`}>
            <i className="fas fa-plus mr-2"></i>
            New Order
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <Card>
          <CardHeader title="Contact Information" />
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-phone"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatPhone(doctor.phone)}</p>
              </div>
            </div>
            {doctor.email && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-lg flex items-center justify-center">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                  <p className="font-medium text-gray-900 dark:text-white">{doctor.email}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Tax Info */}
        <Card>
          <CardHeader title="Tax Information" />
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">GST Number</p>
              <p className="font-medium text-gray-900 dark:text-white font-mono">
                {doctor.gstNumber || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">PAN Number</p>
              <p className="font-medium text-gray-900 dark:text-white font-mono">
                {doctor.panNumber || '-'}
              </p>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <Card>
          <CardHeader title="Quick Stats" />
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {recentOrders?.length ?? 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {recentInvoices?.length ?? 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Invoices</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Addresses */}
      <Card>
          <CardHeader
            title="Addresses"
            action={
              <Button variant="secondary" size="sm" onClick={() => setShowAddAddressModal(true)}>
                <i className="fas fa-plus mr-2"></i>
                Add Address
              </Button>
            }
          />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {doctor.addresses?.map((address) => (
            <div
              key={address.id}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 dark:text-white">{address.label}</span>
                {address.isDefault && <Badge variant="info">Default</Badge>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {address.line1}
                {address.line2 && <>, {address.line2}</>}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {address.city}, {address.state} - {address.pincode}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                State Code: {address.stateCode}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <Card padding="none">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <CardHeader
              title="Recent Work Orders"
              action={
                <Link to={`/work-orders?doctorId=${id}`} className="text-sm text-primary-600 hover:text-primary-700">
                  View all
                </Link>
              }
            />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentOrders?.length ? (
              recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/work-orders/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{order.displayId}</p>
                    <p className="text-sm text-gray-500">{formatDate(order.createdAt)}</p>
                  </div>
                  <Badge
                    variant={
                      order.status === 'completed' || order.status === 'delivered'
                        ? 'success'
                        : order.status === 'in_progress'
                        ? 'info'
                        : 'gray'
                    }
                  >
                    {(order.status || '').replace('_', ' ')}
                  </Badge>
                </Link>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <i className="fas fa-clipboard-list text-2xl mb-2 opacity-50"></i>
                <p>No orders yet</p>
              </div>
            )}
          </div>
        </Card>

        {/* Recent Invoices */}
        <Card padding="none">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <CardHeader
              title="Recent Invoices"
              action={
                <Link to={`/invoices?doctorId=${id}`} className="text-sm text-primary-600 hover:text-primary-700">
                  View all
                </Link>
              }
            />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentInvoices?.length ? (
              recentInvoices.map((invoice) => (
                <Link
                  key={invoice.id}
                  to={`/invoices/${invoice.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{invoice.displayId}</p>
                    <p className="text-sm text-gray-500">{formatDate(invoice.issueDate)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(invoice.totalAmount)}
                    </p>
                    <Badge
                      variant={
                        invoice.status === 'paid'
                          ? 'success'
                          : invoice.status === 'overdue'
                          ? 'error'
                          : invoice.status === 'partial'
                          ? 'warning'
                          : 'gray'
                      }
                    >
                      {invoice.status}
                    </Badge>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <i className="fas fa-file-invoice text-2xl mb-2 opacity-50"></i>
                <p>No invoices yet</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Edit Doctor Modal */}
      {doctor && (
        <EditDoctorModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          doctor={doctor}
          onSuccess={() => {
            setShowEditModal(false)
            queryClient.invalidateQueries({ queryKey: ['doctors', id] })
          }}
        />
      )}

      {/* Add Address Modal */}
      {doctor && (
        <AddAddressModal
          isOpen={showAddAddressModal}
          onClose={() => setShowAddAddressModal(false)}
          doctorId={id!}
          billingAddress={doctor.addresses?.find(a => a.isDefault)}
          onSuccess={() => {
            setShowAddAddressModal(false)
            queryClient.invalidateQueries({ queryKey: ['doctors', id] })
          }}
        />
      )}
    </div>
  )
}

// Edit Doctor Modal Component
interface EditDoctorModalProps {
  isOpen: boolean
  onClose: () => void
  doctor: DoctorDisplay
  onSuccess: () => void
}

function EditDoctorModal({ isOpen, onClose, doctor, onSuccess }: EditDoctorModalProps) {
  // Get shipping and billing addresses
  const shippingAddress = doctor.addresses?.find(a => a.label === 'Shipping') || doctor.addresses?.find(a => a.isDefault) || doctor.addresses?.[0]
  const billingAddress = doctor.addresses?.find(a => a.label === 'Billing')
  
  // Check if billing is same as shipping (addresses match or no billing address)
  const addressesSame = !billingAddress || (
    shippingAddress && 
    billingAddress.line1 === shippingAddress.line1 &&
    billingAddress.city === shippingAddress.city &&
    billingAddress.pincode === shippingAddress.pincode
  )
  
  const [formData, setFormData] = useState({
    name: doctor.name,
    clinicName: doctor.clinicName || '',
    phone: doctor.phone?.replace('+91', '') || '',
    email: doctor.email || '',
    gstNumber: doctor.gstNumber || '',
    panNumber: doctor.panNumber || '',
    // Shipping address
    shippingAddressLine1: shippingAddress?.line1 || '',
    shippingAddressLine2: shippingAddress?.line2 || '',
    shippingCity: shippingAddress?.city || '',
    shippingState: shippingAddress?.state || '',
    shippingPincode: shippingAddress?.pincode || '',
    // Billing address
    billingAddressLine1: billingAddress?.line1 || shippingAddress?.line1 || '',
    billingAddressLine2: billingAddress?.line2 || shippingAddress?.line2 || '',
    billingCity: billingAddress?.city || shippingAddress?.city || '',
    billingState: billingAddress?.state || shippingAddress?.state || '',
    billingPincode: billingAddress?.pincode || shippingAddress?.pincode || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(addressesSame)

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (formData.phone && formData.phone.length !== 10) {
      newErrors.phone = 'Phone must be 10 digits'
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }
    if (formData.shippingPincode && formData.shippingPincode.length !== 6) {
      newErrors.shippingPincode = 'Pincode must be 6 digits'
    }
    if (!billingSameAsShipping && formData.billingPincode && formData.billingPincode.length !== 6) {
      newErrors.billingPincode = 'Pincode must be 6 digits'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Determine billing address values
      const billingAddress = billingSameAsShipping ? {
        billing_address_line1: data.shippingAddressLine1.trim() || null,
        billing_address_line2: data.shippingAddressLine2.trim() || null,
        billing_city: data.shippingCity.trim() || null,
        billing_state: data.shippingState.trim() || null,
        billing_pincode: data.shippingPincode.trim() || null,
      } : {
        billing_address_line1: data.billingAddressLine1.trim() || null,
        billing_address_line2: data.billingAddressLine2.trim() || null,
        billing_city: data.billingCity.trim() || null,
        billing_state: data.billingState.trim() || null,
        billing_pincode: data.billingPincode.trim() || null,
      }
      
      return api.put(`/doctors/${doctor.id}`, {
        name: data.name.trim(),
        clinic_name: data.clinicName.trim() || null,
        phone: data.phone ? `+91${data.phone}` : undefined,
        email: data.email.trim() || null,
        gstin: data.gstNumber.trim() || null,
        pan: data.panNumber.trim() || null,
        // Shipping address
        shipping_address_line1: data.shippingAddressLine1.trim() || null,
        shipping_address_line2: data.shippingAddressLine2.trim() || null,
        shipping_city: data.shippingCity.trim() || null,
        shipping_state: data.shippingState.trim() || null,
        shipping_pincode: data.shippingPincode.trim() || null,
        // Billing address
        ...billingAddress,
        billing_same_as_shipping: billingSameAsShipping,
      })
    },
    onSuccess,
    onError: () => {
      setErrors({ form: 'Failed to update doctor. Please try again.' })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      mutation.mutate(formData)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Doctor" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Doctor Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
          />
          <Input
            label="Clinic Name"
            value={formData.clinicName}
            onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-l-lg text-sm">
                +91
              </span>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                className={`flex-1 px-4 py-2.5 border rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="9876543210"
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
          </div>
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="GST Number"
            value={formData.gstNumber}
            onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })}
            placeholder="22AAAAA0000A1Z5"
          />
          <Input
            label="PAN Number"
            value={formData.panNumber}
            onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
            placeholder="AAAAA0000A"
          />
        </div>

        {/* Shipping Address Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <i className="fas fa-truck text-gray-400"></i>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Shipping Address</span>
          </div>
          
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              value={formData.shippingAddressLine1}
              onChange={(e) => setFormData({ ...formData, shippingAddressLine1: e.target.value })}
              placeholder="123, Main Street"
            />
            <Input
              label="Address Line 2"
              value={formData.shippingAddressLine2}
              onChange={(e) => setFormData({ ...formData, shippingAddressLine2: e.target.value })}
              placeholder="Near Central Park"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="City"
                value={formData.shippingCity}
                onChange={(e) => setFormData({ ...formData, shippingCity: e.target.value })}
                placeholder="Mumbai"
              />
              <Input
                label="State"
                value={formData.shippingState}
                onChange={(e) => setFormData({ ...formData, shippingState: e.target.value })}
                placeholder="Maharashtra"
              />
              <Input
                label="Pincode"
                value={formData.shippingPincode}
                onChange={(e) => {
                  setFormData({ ...formData, shippingPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
                  if (errors.shippingPincode) setErrors({ ...errors, shippingPincode: '' })
                }}
                error={errors.shippingPincode}
                placeholder="400001"
              />
            </div>
          </div>
        </div>

        {/* Billing Address Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <i className="fas fa-file-invoice text-gray-400"></i>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Billing Address</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={billingSameAsShipping}
                onChange={(e) => {
                  setBillingSameAsShipping(e.target.checked)
                  // When checking, copy shipping to billing
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      billingAddressLine1: formData.shippingAddressLine1,
                      billingAddressLine2: formData.shippingAddressLine2,
                      billingCity: formData.shippingCity,
                      billingState: formData.shippingState,
                      billingPincode: formData.shippingPincode,
                    })
                  }
                }}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">Same as shipping</span>
            </label>
          </div>
          
          {!billingSameAsShipping && (
            <div className="space-y-4">
              <Input
                label="Address Line 1"
                value={formData.billingAddressLine1}
                onChange={(e) => setFormData({ ...formData, billingAddressLine1: e.target.value })}
                placeholder="123, Main Street"
              />
              <Input
                label="Address Line 2"
                value={formData.billingAddressLine2}
                onChange={(e) => setFormData({ ...formData, billingAddressLine2: e.target.value })}
                placeholder="Near Central Park"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label="City"
                  value={formData.billingCity}
                  onChange={(e) => setFormData({ ...formData, billingCity: e.target.value })}
                  placeholder="Mumbai"
                />
                <Input
                  label="State"
                  value={formData.billingState}
                  onChange={(e) => setFormData({ ...formData, billingState: e.target.value })}
                  placeholder="Maharashtra"
                />
                <Input
                  label="Pincode"
                  value={formData.billingPincode}
                  onChange={(e) => {
                    setFormData({ ...formData, billingPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
                    if (errors.billingPincode) setErrors({ ...errors, billingPincode: '' })
                  }}
                  error={errors.billingPincode}
                  placeholder="400001"
                />
              </div>
            </div>
          )}
        </div>

        {errors.form && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.form}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Add Address Modal Component
interface AddAddressModalProps {
  isOpen: boolean
  onClose: () => void
  doctorId: string
  billingAddress?: DoctorDisplay['addresses'][0]
  onSuccess: () => void
}

function AddAddressModal({ isOpen, onClose, doctorId, billingAddress, onSuccess }: AddAddressModalProps) {
  const [formData, setFormData] = useState({
    label: 'Shipping',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    stateCode: '',
    pincode: '',
    isDefault: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const copyFromBilling = () => {
    if (billingAddress) {
      setFormData({
        label: 'Shipping',
        addressLine1: billingAddress.line1,
        addressLine2: billingAddress.line2 || '',
        city: billingAddress.city,
        state: billingAddress.state,
        stateCode: billingAddress.stateCode,
        pincode: billingAddress.pincode,
        isDefault: false,
      })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.label.trim()) newErrors.label = 'Label is required'
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required'
    if (!formData.city.trim()) newErrors.city = 'City is required'
    if (!formData.state.trim()) newErrors.state = 'State is required'
    if (!formData.pincode || formData.pincode.length !== 6) newErrors.pincode = 'Valid 6-digit pincode required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.post(`/doctors/${doctorId}/addresses`, {
        label: data.label.trim(),
        address_line1: data.addressLine1.trim(),
        address_line2: data.addressLine2.trim() || null,
        city: data.city.trim(),
        state: data.state.trim(),
        state_code: data.stateCode.trim() || data.state.substring(0, 2).toUpperCase(),
        pincode: data.pincode,
        is_default: data.isDefault,
      })
    },
    onSuccess: () => {
      setFormData({ label: 'Shipping', addressLine1: '', addressLine2: '', city: '', state: '', stateCode: '', pincode: '', isDefault: false })
      onSuccess()
    },
    onError: () => {
      setErrors({ form: 'Failed to add address. Please try again.' })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      mutation.mutate(formData)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Address" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {billingAddress && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <i className="fas fa-copy mr-2"></i>
              Copy details from billing address
            </p>
            <Button type="button" variant="secondary" size="sm" onClick={copyFromBilling}>
              Copy
            </Button>
          </div>
        )}

        <Input
          label="Address Label *"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          error={errors.label}
          placeholder="e.g., Shipping, Clinic 2, Branch Office"
        />

        <Input
          label="Address Line 1 *"
          value={formData.addressLine1}
          onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
          error={errors.addressLine1}
          placeholder="Street address"
        />

        <Input
          label="Address Line 2"
          value={formData.addressLine2}
          onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
          placeholder="Apartment, suite, etc."
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Input
            label="City *"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            error={errors.city}
          />
          <Input
            label="State *"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            error={errors.state}
          />
          <Input
            label="State Code"
            value={formData.stateCode}
            onChange={(e) => setFormData({ ...formData, stateCode: e.target.value.toUpperCase() })}
            placeholder="MH"
            maxLength={2}
          />
          <Input
            label="Pincode *"
            value={formData.pincode}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
            error={errors.pincode}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isDefault"
            checked={formData.isDefault}
            onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="isDefault" className="text-sm text-gray-700 dark:text-gray-300">
            Set as default shipping address
          </label>
        </div>

        {errors.form && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.form}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-plus mr-2"></i>
            Add Address
          </Button>
        </div>
      </form>
    </Modal>
  )
}
